/*global chrome*/

import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import moment from "moment";
import { useGlobalContext } from "../../../../Context/GlobalContext";
import { GetLinkedInCookieStatus } from "../../../../Services/UserDataService";
import { constants, LINKS } from "../../../../Utils/constants";

// Prospect posts are scraped server-side with the LinkedIn session the extension uploads, so a
// missing extension or a stale/rejected session silently stops the feed from updating.

const alertSx = (color) => ({
  border: `1px solid ${color.border}`,
  borderRadius: "1rem",
  color: color.text,
  fontWeight: 500,
  marginBottom: "0.5rem",
  alignItems: "center",
});

const WARNING = { border: "#EAAB3E", text: "#8B5800" };
const ERROR = { border: "#D61F1F", text: "#8A1414" };

const SYNC_RESULT_MESSAGES = {
  no_session: "You're not logged in to LinkedIn in this browser. Log in to LinkedIn, then click Sync now.",
  no_email: "Sign in to the Kynzo extension first, then click Sync now.",
  still_invalid:
    "LinkedIn is still rejecting this session. Log out of LinkedIn and log back in, then click Sync now.",
  failed: "Sync failed. Please try again.",
  outdated:
    "Your Kynzo extension is out of date. Update it, or click the sync icon in the Kynzo side panel on LinkedIn.",
};

// Asks the extension to upload the browser's current LinkedIn session.
const requestExtensionSync = () =>
  new Promise((resolve) => {
    if (typeof chrome === "undefined" || !chrome?.runtime) return resolve(null);
    chrome.runtime.sendMessage(constants.EXTENSION_ID, { type: "syncLinkedInCookie" }, (response) =>
      resolve(chrome.runtime.lastError ? null : response)
    );
  });

const LinkedInConnectionBanner = () => {
  const { isExtensionInstalled, isExtensionCheckDone, checkExtensionInstalled } = useGlobalContext();
  const [cookieStatus, setCookieStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await GetLinkedInCookieStatus();
      if (res.ok) {
        const json = await res.json();
        setCookieStatus(json);
        return json;
      }
    } catch (e) {
      console.log("linkedin cookie status error", e);
    }
    return null;
  }, []);

  const refresh = useCallback(() => {
    checkExtensionInstalled();
    fetchStatus();
  }, [checkExtensionInstalled, fetchStatus]);

  // Re-check on focus: the usual fix is "go open LinkedIn, come back", and the extension
  // uploads the new session within seconds of LinkedIn setting it.
  useEffect(() => {
    refresh();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [refresh]);

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncMessage(null);
    const result = await requestExtensionSync();
    if (result?.uploaded) {
      const status = await fetchStatus();
      if (status?.status === "invalid") setSyncMessage(SYNC_RESULT_MESSAGES.still_invalid);
    } else if (result?.skipped === "no_session" || result?.skipped === "no_email") {
      setSyncMessage(SYNC_RESULT_MESSAGES[result.skipped]);
    } else if (result?.error) {
      setSyncMessage(SYNC_RESULT_MESSAGES.failed);
    } else {
      // Extensions before 1.0.104 answer every message with just { installed: true }.
      setSyncMessage(SYNC_RESULT_MESSAGES.outdated);
    }
    setIsSyncing(false);
  };

  const lastSynced = cookieStatus?.updated_at ? moment(cookieStatus.updated_at).fromNow() : "never";
  const status = cookieStatus?.status;

  let banner = null;
  if (isExtensionCheckDone && !isExtensionInstalled) {
    banner = (
      <Alert severity="warning" sx={alertSx(WARNING)}>
        Install the <b>Kynzo Chrome extension</b> to keep your LinkedIn connected - we need it to
        fetch your prospects' latest posts.{" "}
        <Link href={LINKS.CHROME_EXTENSION} target="_blank" fontWeight={700} color={WARNING.text}>
          Install the extension
        </Link>
      </Alert>
    );
  } else if (status === "invalid" || status === "stale" || status === "missing") {
    const color = status === "invalid" ? ERROR : WARNING;
    banner = (
      <Alert
        severity={status === "invalid" ? "error" : "warning"}
        sx={alertSx(color)}
        action={
          <Button
            variant="contained"
            size="small"
            disabled={isSyncing}
            onClick={handleSync}
            sx={{
              backgroundColor: color.border,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "0.5rem",
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: color.text },
            }}
          >
            {isSyncing ? "Syncing..." : "Sync now"}
          </Button>
        }
      >
        {status === "invalid"
          ? "LinkedIn rejected your saved connection, so your feed isn't updating. "
          : "Your LinkedIn connection needs a refresh. "}
        Make sure you're logged in to{" "}
        <Link href="https://www.linkedin.com/feed/" target="_blank" fontWeight={700} color={color.text}>
          LinkedIn
        </Link>{" "}
        in this browser, then click <b>Sync now</b>.
        {syncMessage && <Box sx={{ marginTop: "0.25rem", fontWeight: 700 }}>{syncMessage}</Box>}
      </Alert>
    );
  }

  if (!cookieStatus && !banner) return null;

  return (
    <Box>
      {banner}
      {cookieStatus && (
        <Typography sx={{ fontSize: "12px", color: "#415058" }}>
          LinkedIn last synced: {lastSynced}
        </Typography>
      )}
    </Box>
  );
};

export default LinkedInConnectionBanner;
